import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api, { apiErrorMessage } from '@/lib/api';
import AdminLayout from '@/components/AdminLayout';
import { toast } from 'sonner';
import {
    Loader2, Search, Mail, Phone, Building2, Award, MessageSquare,
    CheckCircle2, X, ExternalLink, Trash2, AlertTriangle, Send, UserPlus,
} from 'lucide-react';

const VIOLET = '#7C6BEF';
const RED = '#DC2626';
const EMERALD = '#059669';

/** AdminContacts — Lista de solicitudes de contacto del landing, con detalle modal. */
export default function AdminContacts() {
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [search, setSearch] = useState('');
    const [detail, setDetail] = useState(null);
    const [toDelete, setToDelete] = useState(null); // {id, name} pending confirmation
    const [deleting, setDeleting] = useState(false);
    // Iter 45 · onboarding email preview + send.
    const [onboardingPreview, setOnboardingPreview] = useState(null); // { contact, preview }

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('/admin/contacts');
            setRows(r.data || []);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al cargar solicitudes'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    // Auto-open detail if ?open=<id>
    useEffect(() => {
        const openId = params.get('open');
        if (openId && rows.length > 0) {
            const found = rows.find((r) => r.id === openId);
            if (found) {
                openDetail(found);
                params.delete('open');
                setParams(params, { replace: true });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    const openDetail = async (row) => {
        try {
            const r = await api.get(`/admin/contacts/${row.id}`);
            setDetail(r.data);
            setRows((prev) => prev.map((x) => (x.id === row.id ? { ...x, read: true } : x)));
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al abrir la solicitud'));
        }
    };

    const confirmDelete = async () => {
        if (!toDelete) return;
        setDeleting(true);
        try {
            await api.delete(`/admin/contacts/${toDelete.id}`);
            setRows((prev) => prev.filter((r) => r.id !== toDelete.id));
            if (detail?.id === toDelete.id) setDetail(null);
            toast.success('Mensaje eliminado.');
            setToDelete(null);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al eliminar el mensaje.'));
        } finally {
            setDeleting(false);
        }
    };

    // Iter 45 · onboarding — build preview, then user confirms send.
    const askSendOnboarding = async (contact) => {
        if (!contact.email) {
            toast.error('El contacto no tiene un correo válido');
            return;
        }
        try {
            const r = await api.post(`/admin/contacts/${contact.id}/onboarding-preview`);
            setOnboardingPreview({ contact, preview: r.data });
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al generar la vista previa'));
        }
    };

    // Iter 45 · Dar de alta — jump to /admin/clients with the contact id;
    // the client page reads ?fromContact=… and pre-fills the New Client modal
    // with the onboarding_data (see AdminClients.jsx). Keeps modal logic single-source.
    const giveHighSignUp = (contact) => {
        navigate(`/admin/clients?openNew=1&fromContact=${contact.id}`);
    };

    const filtered = useMemo(() => {
        let base = rows;
        // Iter 48 · Dashboard badge target: unanswered ≥ 48h without onboarding action.
        if (params.get('filter') === 'unanswered_48h') {
            const cutoff = Date.now() - 48 * 60 * 60 * 1000;
            base = rows.filter((r) => {
                if (r.deleted) return false;
                const ob = r.onboarding_status;
                if (ob && ob !== 'not_sent') return false;
                const created = r.created_at ? new Date(r.created_at).getTime() : 0;
                return created > 0 && created <= cutoff;
            });
        }
        if (!search) return base;
        const q = search.toLowerCase();
        return base.filter((r) =>
            (r.name || '').toLowerCase().includes(q)
            || (r.email || '').toLowerCase().includes(q)
            || (r.industry || '').toLowerCase().includes(q)
            || (r.message || '').toLowerCase().includes(q),
        );
    }, [rows, search, params]);

    const unreadCount = rows.filter((r) => !r.read).length;

    return (
        <AdminLayout title="Solicitudes de contacto">
            <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <p className="text-sm text-gray-500">
                        Prospectos que llenaron el formulario del landing.
                        {unreadCount > 0 && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 text-xs font-bold text-white" style={{ background: VIOLET }} data-testid="contacts-unread-badge">
                                {unreadCount} sin leer
                            </span>
                        )}
                    </p>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 border w-full sm:w-80" style={{ borderColor: 'var(--border)' }}>
                    <Search className="w-4 h-4 text-gray-400" />
                    <input
                        type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar nombre, correo, giro…"
                        data-testid="contacts-search"
                        className="w-full bg-transparent outline-none text-sm"
                    />
                </div>
            </div>

            {loading ? (
                <div className="py-16 text-center text-gray-500 text-sm">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Cargando…
                </div>
            ) : filtered.length === 0 ? (
                <div className="py-16 text-center text-gray-400 text-sm italic border" style={{ borderColor: 'var(--border)' }}>
                    <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-30" />
                    {rows.length === 0 ? 'No hay solicitudes aún.' : 'Sin resultados con esa búsqueda.'}
                </div>
            ) : (
                <div className="border" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} data-testid="contacts-list">
                    {filtered.map((r) => (
                        <div
                            key={r.id}
                            className="flex items-start gap-4 p-4 border-b last:border-b-0 hover:bg-black/[.02] dark:hover:bg-white/[.04]"
                            style={{ borderColor: 'var(--border)' }}
                            data-testid={`contact-row-${r.id}`}
                        >
                            <button
                                type="button"
                                onClick={() => openDetail(r)}
                                className="flex items-start gap-4 flex-1 min-w-0 text-left"
                                data-testid={`contact-row-open-${r.id}`}
                            >
                                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0" style={{ background: r.read ? '#9CA3AF' : VIOLET }}>
                                    {(r.name || 'C').charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={r.read ? 'font-medium' : 'font-black'}>{r.name}</span>
                                        {!r.read && <span className="inline-block w-2 h-2 rounded-full" style={{ background: VIOLET }} />}
                                        <span className="text-xs text-gray-500">· {r.email}</span>
                                        {r.onboarding_status === 'sent' && (
                                            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold px-2 py-0.5"
                                                style={{ background: 'rgba(124,107,239,0.12)', color: VIOLET }}
                                                data-testid={`contact-status-sent-${r.id}`}>
                                                Formulario enviado
                                            </span>
                                        )}
                                        {r.onboarding_status === 'received' && (
                                            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold px-2 py-0.5"
                                                style={{ background: 'rgba(5,150,105,0.14)', color: EMERALD }}
                                                data-testid={`contact-status-received-${r.id}`}>
                                                Datos recibidos · pendiente de alta
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-3 flex-wrap">
                                        {r.industry && <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {r.industry}</span>}
                                        {r.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {r.phone}</span>}
                                    </div>
                                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{r.message}</div>
                                </div>
                            </button>
                            <div className="flex items-center gap-2 shrink-0">
                                <div className="text-xs text-gray-500">{formatDate(r.created_at)}</div>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setToDelete({ id: r.id, name: r.name }); }}
                                    data-testid={`contact-delete-${r.id}`}
                                    title="Eliminar mensaje"
                                    className="p-1.5 hover:bg-red-500/10 text-gray-400 hover:text-red-500 transition-colors"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {detail && (
                <ContactDetailModal
                    contact={detail}
                    onClose={() => setDetail(null)}
                    onDelete={() => setToDelete({ id: detail.id, name: detail.name })}
                    onAskSendOnboarding={() => askSendOnboarding(detail)}
                    onGiveHighSignUp={() => giveHighSignUp(detail)}
                    onReplied={async () => {
                        // Iter 67 · re-fetch the contact so the replies history
                        // shows the new entry immediately without closing.
                        try {
                            const r = await api.get(`/admin/contacts/${detail.id}`);
                            setDetail(r.data);
                        } catch { /* silent — user still gets the success toast */ }
                    }}
                />
            )}

            {toDelete && (
                <DeleteConfirmModal
                    name={toDelete.name}
                    saving={deleting}
                    onCancel={() => setToDelete(null)}
                    onConfirm={confirmDelete}
                />
            )}

            {onboardingPreview && (
                <OnboardingPreviewModal
                    contact={onboardingPreview.contact}
                    preview={onboardingPreview.preview}
                    onClose={() => setOnboardingPreview(null)}
                    onSent={() => { setOnboardingPreview(null); load(); toast.success('Formulario enviado al prospecto'); }}
                />
            )}
        </AdminLayout>
    );
}

function DeleteConfirmModal({ name, saving, onCancel, onConfirm }) {
    return (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel} data-testid="contact-delete-confirm">
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md" style={{ background: 'var(--card-bg)', border: `2px solid ${RED}` }}>
                <div className="px-6 py-4 flex items-start gap-3 border-b" style={{ borderColor: 'var(--border)' }}>
                    <div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ background: `${RED}18`, color: RED }}>
                        <AlertTriangle className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="font-display text-lg font-bold">¿Eliminar este mensaje?</h3>
                        <p className="text-sm text-gray-500 mt-1">
                            De <span className="font-bold">{name || 'este contacto'}</span>. No se puede deshacer desde el panel.
                        </p>
                    </div>
                </div>
                <div className="px-6 py-4 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={saving}
                        className="px-4 py-2 text-sm font-medium border hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-50"
                        style={{ borderColor: 'var(--border)' }}
                        data-testid="contact-delete-cancel"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={saving}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                        style={{ background: RED }}
                        data-testid="contact-delete-confirm-btn"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        Eliminar
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Iter 45 · Onboarding email preview + confirm-send modal.
 */
function OnboardingPreviewModal({ contact, preview, onClose, onSent }) {
    const [busy, setBusy] = useState(false);
    const doSend = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/contacts/${contact.id}/send-onboarding`);
            onSent();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al enviar el correo'));
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            onClick={busy ? undefined : onClose}
            data-testid="onboarding-preview-modal">
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl my-8"
                style={{ background: 'var(--card-bg)', border: `2px solid ${VIOLET}` }}>
                <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: VIOLET }}>
                            Vista previa · Envío de formulario
                        </div>
                        <h3 className="font-display text-xl font-black">{preview.subject}</h3>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-black/5 dark:hover:bg-white/10">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-6 space-y-4 text-sm">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="border p-3" style={{ borderColor: 'var(--border)' }}>
                            <div className="text-[9px] uppercase tracking-widest font-bold text-gray-500">Destinatario</div>
                            <div className="font-mono text-xs mt-1 break-all">{preview.to}</div>
                        </div>
                        <div className="border p-3" style={{ borderColor: 'var(--border)' }}>
                            <div className="text-[9px] uppercase tracking-widest font-bold text-gray-500">Giro detectado</div>
                            <div className="text-xs mt-1">
                                {preview.industry || <span className="italic text-gray-500">Sin giro — se enviarán todos los campos</span>}
                            </div>
                            <div className="text-[10px] text-gray-500 mt-1">
                                {preview.field_count} campos de datos generales
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">Cuerpo del correo</div>
                        <div className="border max-h-80 overflow-y-auto"
                            style={{ borderColor: 'var(--border)', background: '#F0F0F3' }}
                            dangerouslySetInnerHTML={{ __html: preview.html }} />
                    </div>
                    <div className="text-xs text-gray-500">
                        El enlace es de un solo uso y expira en 7 días. La respuesta del prospecto
                        aparecerá aquí mismo con el estado <strong>Datos recibidos</strong>.
                    </div>
                </div>
                <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: 'var(--border)' }}>
                    <button onClick={onClose} disabled={busy}
                        className="px-4 py-2 text-sm font-bold border hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50"
                        style={{ borderColor: 'var(--border)' }}>
                        Cancelar
                    </button>
                    <button onClick={doSend} disabled={busy}
                        data-testid="onboarding-preview-send-btn"
                        className="px-4 py-2 text-sm font-bold text-white inline-flex items-center gap-2 disabled:opacity-50"
                        style={{ background: VIOLET }}>
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Enviar formulario
                    </button>
                </div>
            </div>
        </div>
    );
}


function ContactDetailModal({ contact, onClose, onDelete, onAskSendOnboarding, onGiveHighSignUp, onReplied }) {
    const roleLabel = {
        client: 'Cliente principal / Establecimiento',
        provider: 'Proveedor',
        other: 'Otro / Información general',
    }[contact.role] || (contact.role || '—');

    // Resolved certification entries: [{id, code, name, is_active, orphan?}]
    const certs = Array.isArray(contact.certification_interests_resolved)
        ? contact.certification_interests_resolved
        : [];

    const onboardingStatus = contact.onboarding_status || null; // null | "sent" | "received"
    const receivedData = contact.onboarding_data || null;
    const replies = Array.isArray(contact.replies) ? contact.replies : [];

    // Iter 67 · in-app reply modal state. Replaces the previous mailto: handoff.
    const [replyOpen, setReplyOpen] = useState(false);

    // Iter 67 · The mailto: builder was removed. Replies now use an in-app
    // modal that posts to POST /admin/contacts/{id}/reply and delivers via
    // the existing SMTP admin mailbox, matching the transactional pattern.

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose} data-testid="contact-detail-modal">
            <div onClick={(e) => e.stopPropagation()}
                className="w-full max-w-xl flex flex-col max-h-[calc(100vh-2rem)]"
                style={{ background: 'var(--card-bg)', border: `2px solid ${VIOLET}` }}>
                <div className="flex justify-between items-start px-6 py-4 border-b shrink-0"
                    style={{ borderColor: 'var(--border)' }}
                    data-testid="contact-detail-header">
                    <div className="min-w-0 flex-1 pr-3">
                        <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: VIOLET }}>Solicitud de contacto</div>
                        <h3 className="font-display text-xl font-black break-words">{contact.name}</h3>
                        <div className="text-xs text-gray-500 mt-0.5">{formatDate(contact.created_at)}</div>
                    </div>
                    <button type="button" onClick={onClose} data-testid="contact-detail-close"
                        className="p-1 hover:bg-black/5 dark:hover:bg-white/10 shrink-0">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="px-6 py-4 space-y-3 overflow-y-auto flex-1 min-h-0" data-testid="contact-detail-body">
                    <DetailRow icon={Mail} label="Correo" value={
                        <a href={`mailto:${contact.email}`} className="hover:underline inline-flex items-center gap-1" style={{ color: VIOLET }}>
                            {contact.email} <ExternalLink className="w-3 h-3" />
                        </a>
                    } />
                    <DetailRow icon={Phone} label="Teléfono" value={contact.phone || '—'} />
                    <DetailRow icon={Building2} label="Giro / Sector" value={contact.industry || '—'} />
                    <DetailRow icon={Award} label="Distintivos de interés" value={
                        certs.length === 0 ? '—' : (
                            <div className="flex flex-wrap gap-1.5" data-testid="contact-detail-certs">
                                {certs.map((c) => {
                                    const label = c.orphan
                                        ? c.code   // unknown slug / removed ref
                                        : (c.name || c.code);
                                    const inactive = c.is_active === false && !c.orphan;
                                    const orphan = c.orphan === true;
                                    return (
                                        <span
                                            key={c.id}
                                            title={c.name || c.code}
                                            data-testid={`contact-detail-cert-${c.id}`}
                                            className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full"
                                            style={{
                                                background: orphan
                                                    ? 'rgba(107,114,128,0.15)'
                                                    : inactive
                                                        ? 'rgba(220,38,38,0.12)'
                                                        : `${VIOLET}18`,
                                                color: orphan ? '#6B7280' : inactive ? '#DC2626' : VIOLET,
                                            }}
                                        >
                                            {label}
                                            {inactive && <span className="text-[9px] font-medium opacity-80">(inactiva)</span>}
                                            {orphan && <span className="text-[9px] font-medium opacity-80">(legada)</span>}
                                        </span>
                                    );
                                })}
                            </div>
                        )
                    } />
                    <DetailRow icon={CheckCircle2} label="Perfil" value={roleLabel} />
                    {onboardingStatus && (
                        <div className="pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">Estado del onboarding</div>
                            {onboardingStatus === 'sent' && (
                                <div className="text-xs p-3 border" style={{ borderColor: 'var(--border)', background: 'rgba(124,107,239,0.06)', color: VIOLET }}>
                                    Formulario enviado {formatDate(contact.onboarding_sent_at)} — esperando respuesta del prospecto.
                                    {contact.onboarding_expires_at && <> Expira {formatDate(contact.onboarding_expires_at)}.</>}
                                </div>
                            )}
                            {onboardingStatus === 'received' && receivedData && (
                                <div className="text-xs p-3 border space-y-1" style={{ borderColor: EMERALD, background: 'rgba(5,150,105,0.06)' }}>
                                    <div className="font-bold" style={{ color: EMERALD }}>
                                        Datos recibidos {formatDate(contact.onboarding_received_at)} — listo para dar de alta
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 pt-2">
                                        {Object.entries(receivedData).slice(0, 12).map(([k, v]) => (
                                            <div key={k} className="min-w-0">
                                                <div className="text-[9px] uppercase tracking-wider font-bold text-gray-500 truncate">{k}</div>
                                                <div className="text-xs truncate">{String(v || '—')}</div>
                                            </div>
                                        ))}
                                    </div>
                                    {Object.keys(receivedData).length > 12 && (
                                        <div className="text-[10px] text-gray-500 pt-1">
                                            + {Object.keys(receivedData).length - 12} campos más — se verán completos al dar de alta
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    <div className="pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">Mensaje</div>
                        <div className="text-sm whitespace-pre-wrap p-3 border" style={{ borderColor: 'var(--border)', background: 'rgba(124,107,239,0.06)' }}>
                            {contact.message}
                        </div>
                    </div>
                    {replies.length > 0 && (
                        <div className="pt-3 border-t" style={{ borderColor: 'var(--border)' }} data-testid="contact-replies-history">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">
                                Historial de respuestas ({replies.length})
                            </div>
                            <div className="space-y-2">
                                {replies.slice().reverse().map((r) => (
                                    <div key={r.id} className="p-3 border text-sm"
                                        style={{ borderColor: 'var(--border)', background: 'rgba(5,150,105,0.04)' }}
                                        data-testid={`contact-reply-item-${r.id}`}>
                                        <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                                            <div className="font-bold text-xs" style={{ color: EMERALD }}>{r.subject}</div>
                                            <div className="text-[10px] text-gray-500">{formatDate(r.sent_at)}</div>
                                        </div>
                                        <div className="text-[11px] text-gray-500 mb-2 truncate">de {r.sent_by_email || '—'}</div>
                                        <div className="text-sm whitespace-pre-wrap opacity-90">{r.body}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <div className="px-6 py-4 border-t flex flex-wrap justify-between items-center gap-2 shrink-0" style={{ borderColor: 'var(--border)' }}>
                    <button
                        type="button"
                        onClick={onDelete}
                        data-testid="contact-detail-delete-btn"
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border hover:bg-red-500/10"
                        style={{ borderColor: 'var(--border)', color: RED }}
                    >
                        <Trash2 className="w-4 h-4" /> Eliminar
                    </button>
                    <div className="flex flex-wrap gap-2 justify-end">
                        {onboardingStatus === 'received' ? (
                            <button type="button" onClick={onGiveHighSignUp}
                                data-testid="contact-give-signup-btn"
                                className="inline-flex items-center gap-2 px-4 py-2 text-white font-bold text-sm hover:opacity-90"
                                style={{ background: EMERALD }}>
                                <UserPlus className="w-4 h-4" /> Dar de alta
                            </button>
                        ) : (
                            <button type="button" onClick={onAskSendOnboarding}
                                data-testid="contact-send-onboarding-btn"
                                disabled={!contact.email}
                                title={contact.email ? '' : 'El contacto no tiene un correo válido'}
                                className="inline-flex items-center gap-2 px-4 py-2 border-2 font-bold text-sm hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
                                style={{ borderColor: VIOLET, color: VIOLET }}>
                                <Send className="w-4 h-4" />
                                {onboardingStatus === 'sent' ? 'Reenviar formulario' : 'Enviar formulario de datos'}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => setReplyOpen(true)}
                            disabled={!contact.email}
                            title={contact.email ? '' : 'El contacto no tiene un correo válido'}
                            className="inline-flex items-center gap-2 px-4 py-2 text-white font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                            style={{ background: VIOLET }}
                            data-testid="contact-reply-btn"
                        >
                            <Mail className="w-4 h-4" /> Responder
                        </button>
                    </div>
                </div>
            </div>
            {replyOpen && (
                <ContactReplyModal
                    contact={contact}
                    onClose={() => setReplyOpen(false)}
                    onSent={(reply) => {
                        setReplyOpen(false);
                        // Bubble up so parent list refreshes and the same modal, when reopened,
                        // shows the new entry in the replies history.
                        if (onReplied) onReplied(reply);
                    }}
                />
            )}
        </div>
    );
}

function ContactReplyModal({ contact, onClose, onSent }) {
    const defaultSubject = 'Re: tu solicitud a AIGIS Certificate';
    const [subject, setSubject] = useState(defaultSubject);
    const [body, setBody] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');

    const submit = async () => {
        setError('');
        if (!body.trim()) {
            setError('El mensaje no puede estar vacío.');
            return;
        }
        setSending(true);
        try {
            const r = await api.post(`/admin/contacts/${contact.id}/reply`, {
                subject: subject.trim() || defaultSubject,
                body,
            });
            toast.success('Respuesta enviada.');
            if (onSent) onSent(r.data.reply);
        } catch (e) {
            setError(apiErrorMessage(e, 'No se pudo enviar la respuesta.'));
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            onClick={sending ? undefined : onClose}
            data-testid="contact-reply-modal">
            <div className="w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()}
                style={{ background: 'var(--card-bg)', border: `2px solid ${VIOLET}` }}>
                <div className="flex justify-between items-start px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: VIOLET }}>Responder</div>
                        <h3 className="font-display text-xl font-black">A {contact.name}</h3>
                    </div>
                    <button type="button" onClick={onClose} disabled={sending}
                        data-testid="contact-reply-close"
                        className="p-1 hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-40">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="px-6 py-4 space-y-3">
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Destinatario</label>
                        <input type="email" value={contact.email || ''} readOnly
                            data-testid="contact-reply-to"
                            className="w-full px-3 py-2 border text-sm opacity-70 cursor-not-allowed"
                            style={{ borderColor: 'var(--border)', background: 'rgba(0,0,0,0.02)' }} />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Asunto</label>
                        <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                            disabled={sending} maxLength={200}
                            data-testid="contact-reply-subject"
                            className="w-full px-3 py-2 border text-sm"
                            style={{ borderColor: 'var(--border)' }} />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Mensaje</label>
                        <textarea value={body} onChange={(e) => setBody(e.target.value)}
                            disabled={sending} rows={8} maxLength={10000}
                            placeholder="Escribe tu respuesta al cliente…"
                            data-testid="contact-reply-body"
                            className="w-full px-3 py-2 border text-sm resize-y"
                            style={{ borderColor: 'var(--border)' }} />
                        <div className="text-[10px] text-gray-500 mt-1">{body.length}/10000</div>
                    </div>
                    {error && (
                        <div className="p-3 border text-sm" data-testid="contact-reply-error"
                            style={{ borderColor: RED, background: 'rgba(220,38,38,0.06)', color: RED }}>
                            <div className="flex items-start gap-2">
                                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                <div>{error}</div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: 'var(--border)' }}>
                    <button onClick={onClose} disabled={sending}
                        className="px-4 py-2 text-sm font-bold border hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50"
                        style={{ borderColor: 'var(--border)' }}>
                        Cancelar
                    </button>
                    <button onClick={submit} disabled={sending || !body.trim()}
                        data-testid="contact-reply-send"
                        className="px-4 py-2 text-sm font-bold text-white inline-flex items-center gap-2 disabled:opacity-50"
                        style={{ background: VIOLET }}>
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        {sending ? 'Enviando…' : 'Enviar'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function DetailRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-3">
            <div className="w-8 h-8 flex items-center justify-center shrink-0" style={{ background: 'rgba(124,107,239,0.12)', color: VIOLET }}>
                <Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">{label}</div>
                <div className="text-sm break-words">{value}</div>
            </div>
        </div>
    );
}

function formatDate(iso) {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
        return iso;
    }
}
